import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import './Constellation.css';

export interface StarNode {
  slug: string;
  label: string;
  /** Center of the star in viewBox coordinates (0..720, 0..430). */
  x: number;
  y: number;
  /** Which side the text label sits relative to the star. */
  side: 'left' | 'right';
}

interface ConstellationProps {
  /** Base path the node slug is appended to when clicked. */
  basePath?: string;
  className?: string;
}

const VB_W = 720;
const VB_H = 430;

/** The six key readiness-review capabilities, laid out as a constellation. */
const NODES: StarNode[] = [
  { slug: 'xvt-shipment', label: 'XVT shipment', x: 108, y: 150, side: 'right' },
  { slug: 'xvt-build', label: 'XVT build', x: 250, y: 92, side: 'right' },
  { slug: 'safe-ramp', label: 'Safe Ramp', x: 378, y: 202, side: 'right' },
  { slug: 'ok2-production-tool', label: 'Ok2 Production tool', x: 512, y: 118, side: 'left' },
  { slug: 'safe-transition', label: 'Safe Transition', x: 618, y: 236, side: 'left' },
  { slug: 'spor-selection', label: 'SPOR Selection', x: 300, y: 336, side: 'right' },
];

/** Edges between node indices that draw the constellation outline. */
const EDGES: Array<[number, number]> = [
  [0, 1],
  [1, 2],
  [2, 3],
  [3, 4],
  [2, 5],
  [5, 0],
];

/** Deterministic PRNG so the background starfield is stable across renders. */
function mulberry32(seed: number) {
  let a = seed;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

interface BgStar {
  x: number;
  y: number;
  r: number;
  o: number;
  delay: number;
}

function buildStarfield(): BgStar[] {
  const rnd = mulberry32(20260714);
  const stars: BgStar[] = [];
  for (let i = 0; i < 110; i += 1) {
    stars.push({
      x: rnd() * VB_W,
      y: rnd() * VB_H,
      r: 0.5 + rnd() * 1.9,
      o: 0.2 + rnd() * 0.65,
      delay: rnd() * 5,
    });
  }
  return stars;
}

/**
 * A night-sky constellation diagram. The six key readiness-review
 * capabilities are the bright stars; constellation lines connect them over a
 * starfield with a soft milky-way glow. Clicking a star opens its page.
 */
export function Constellation({
  basePath = '/portal/sqm-consultant',
  className,
}: ConstellationProps) {
  const navigate = useNavigate();
  const stars = useMemo(buildStarfield, []);

  // Path that walks the main chain of stars — a light pulse travels along it.
  const chainPath = useMemo(() => {
    const chain = [0, 1, 2, 3, 4];
    return chain
      .map((idx, i) => `${i === 0 ? 'M' : 'L'}${NODES[idx].x} ${NODES[idx].y}`)
      .join(' ');
  }, []);

  return (
    <div className={`constellation${className ? ` ${className}` : ''}`}>
      <svg
        className="constellation__svg"
        viewBox={`0 0 ${VB_W} ${VB_H}`}
        role="img"
        aria-label="Constellation of the SQM Agent Core with six key capability stars"
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          <radialGradient id="milkyGrad" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#a78bfa" stopOpacity="0.34" />
            <stop offset="60%" stopColor="#7c6cf0" stopOpacity="0.1" />
            <stop offset="100%" stopColor="#7c6cf0" stopOpacity="0" />
          </radialGradient>
          <filter id="milkyBlur" x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur stdDeviation="26" />
          </filter>
          <filter id="lineGlow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="1.6" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Soft milky-way band (the module background supplies the night sky) */}
        <g filter="url(#milkyBlur)">
          <ellipse
            cx="380"
            cy="215"
            rx="360"
            ry="120"
            fill="url(#milkyGrad)"
            transform="rotate(-18 380 215)"
          />
        </g>

        {/* ---------- Background starfield ---------- */}
        <g className="constellation__field">
          {stars.map((s, i) => (
            <circle
              key={`s-${i}`}
              className="constellation__bgstar"
              cx={s.x}
              cy={s.y}
              r={s.r}
              style={{ opacity: s.o, animationDelay: `${s.delay}s` }}
            />
          ))}
        </g>

        {/* ---------- Constellation lines ---------- */}
        <g filter="url(#lineGlow)">
          {EDGES.map(([a, b], i) => (
            <line
              key={`e-${i}`}
              className="constellation__line"
              x1={NODES[a].x}
              y1={NODES[a].y}
              x2={NODES[b].x}
              y2={NODES[b].y}
            />
          ))}
        </g>

        {/* Traveling light along the main chain */}
        <circle className="constellation__pulse" r="2.6">
          <animateMotion dur="5s" repeatCount="indefinite" path={chainPath} />
        </circle>
      </svg>

      {/* ---------- Interactive star overlay (HTML for a11y) ---------- */}
      <div className="constellation__nodes">
        {NODES.map((n, i) => (
          <button
            key={n.slug}
            type="button"
            className={`constellation__node constellation__node--${n.side}`}
            style={{
              left: `${(n.x / VB_W) * 100}%`,
              top: `${(n.y / VB_H) * 100}%`,
              animationDelay: `${i * 0.4}s`,
            }}
            onClick={() => navigate(`${basePath}/${n.slug}`)}
            aria-label={`Open ${n.label}`}
          >
            <span className="constellation__star" aria-hidden>
              <span className="constellation__core" />
            </span>
            <span className="constellation__label">{n.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
